import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PenLine, Loader2, Megaphone, Type, PanelBottom, ScrollText, Image as ImageIcon, Sparkles } from "lucide-react";
import aapi, { formatApiError } from "./aapi";
import { refreshLogo } from "@/components/Logo";
import { PageHeader, inputCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

const HERO_FIELDS = [
  { k: "hero_eyebrow", label: "Eyebrow line" },
  { k: "hero_line_1", label: "Headline — line 1" },
  { k: "hero_line_2", label: "Headline — line 2" },
  { k: "hero_line_gold", label: "Headline — gold word" },
  { k: "hero_subline", label: "Serif subline" },
  { k: "hero_paragraph", label: "Hero paragraph", textarea: true },
];

const FOOTER_FIELDS = [
  { k: "footer_headline_1", label: "Finale headline — line 1" },
  { k: "footer_headline_2", label: "Finale headline — gold line" },
  { k: "footer_text", label: "Finale paragraph", textarea: true },
  { k: "contact_email", label: "Contact email" },
];

const LEGAL_FIELDS = [
  { k: "terms_text", label: "Terms & Conditions — full text (blank line between paragraphs; headings like '1. TITLE')", textarea: true },
];

const BRAND_STORY_FIELDS = [
  { k: "founder_name", label: "Founder name (Mimi AI tells this to users)" },
  { k: "company_about", label: "About the company (Mimi AI's brand knowledge)", textarea: true },
];

export default function AdminContent() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    aapi.get("/content").then(({ data }) => setForm(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await aapi.put("/content", form);
      refreshLogo();
      toast.success("Website content updated — live on the homepage now");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!form)
    return (
      <div className="flex justify-center py-32">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
      </div>
    );

  const renderFields = (fields, prefix) =>
    fields.map((f) => (
      <div key={f.k}>
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{f.label}</label>
        {f.textarea ? (
          <textarea rows={3} value={form[f.k] || ""} onChange={(e) => set(f.k, e.target.value)}
            className={`${inputCls} mt-2 resize-none`} data-testid={`content-${f.k}`} />
        ) : (
          <input value={form[f.k] || ""} onChange={(e) => set(f.k, e.target.value)}
            className={`${inputCls} mt-2`} data-testid={`content-${f.k}`} />
        )}
      </div>
    ));

  return (
    <div data-testid="admin-content-page">
      <PageHeader title="Website Content" sub="Rewrite the homepage and footer — no code, no deploys. Changes go live instantly." />

      <div className="space-y-6">
        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="rounded-2xl border border-gold/25 bg-panel/80 p-6 sm:p-8" data-testid="logo-card">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <ImageIcon size={17} className="text-gold" /> Brand logo
          </h3>
          <p className="mt-2 text-xs leading-relaxed text-neutral-500">
            Used across the website, both panels, emails and every generated PDF (statements, affidavits, revenue reports).
            Square PNG/JPG, max 400 KB. Leave empty to use the default logo. PUBLISH CHANGES applies it everywhere.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-5">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-gold/30 bg-white/[0.03]">
              {form.logo_data
                ? <img src={form.logo_data} alt="Custom logo" className="h-full w-full object-cover" data-testid="logo-preview" />
                : <span className="font-mono text-[9px] text-neutral-600">DEFAULT</span>}
            </div>
            <div className="flex flex-wrap gap-3">
              <label className="cursor-pointer rounded-xl border border-gold/40 px-5 py-3 text-xs font-semibold text-goldlight transition-colors hover:bg-gold hover:text-black" data-testid="logo-upload-label">
                {form.logo_data ? "Replace logo" : "Upload logo"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="logo-upload-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 400 * 1024) return toast.error("Logo must be under 400 KB");
                    const reader = new FileReader();
                    reader.onload = () => set("logo_data", reader.result);
                    reader.readAsDataURL(file);
                  }} />
              </label>
              {form.logo_data && (
                <button onClick={() => set("logo_data", "")} data-testid="logo-remove-button"
                  className="rounded-xl border border-rose-400/40 px-5 py-3 text-xs font-semibold text-rose-300 transition-colors hover:bg-rose-400/10">
                  Remove (use default)
                </button>
              )}
            </div>
          </div>
        </motion.section>
        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
              <Megaphone size={17} className="text-gold" /> Announcement banner
            </h3>
            <button onClick={() => set("banner_enabled", !form.banner_enabled)} data-testid="content-banner-toggle"
              aria-label="Toggle banner"
              className={`relative h-7 w-12 rounded-full transition-colors duration-300 ${form.banner_enabled ? "bg-gold" : "bg-white/10"}`}>
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all duration-300 ${form.banner_enabled ? "left-6" : "left-1"}`} />
            </button>
          </div>
          <p className="mt-2 text-xs text-neutral-500">A gold strip pinned to the top of the homepage — for launches, offers and alerts.</p>
          <div className="mt-4">
            <input value={form.banner_text || ""} onChange={(e) => set("banner_text", e.target.value)}
              placeholder="e.g. FESTIVE WEEK — 10% OFF ALL INSTAGRAM SERVICES"
              className={inputCls} data-testid="content-banner-text" />
          </div>
          {form.banner_enabled && form.banner_text && (
            <div className="mt-4 rounded-xl border border-gold/30 bg-gradient-to-r from-gold/15 via-gold/25 to-gold/15 px-4 py-2.5 text-center font-mono text-[11px] tracking-[0.15em] text-goldlight" data-testid="content-banner-preview">
              {form.banner_text}
            </div>
          )}
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.08 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8" data-testid="brand-story-card">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <Sparkles size={17} className="text-gold" /> Brand story &amp; Mimi AI knowledge
          </h3>
          <p className="mt-2 text-xs leading-relaxed text-neutral-500">
            Mimi AI answers founder/company questions from this. Keep it warm and factual.
          </p>
          <div className="mt-6 space-y-5">
            {renderFields(BRAND_STORY_FIELDS)}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.08 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <Type size={17} className="text-gold" /> Hero section
          </h3>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {renderFields(HERO_FIELDS)}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.16 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <PanelBottom size={17} className="text-gold" /> Footer & contact
          </h3>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {renderFields(FOOTER_FIELDS)}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.24 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <ScrollText size={17} className="text-gold" /> Legal — Terms page (/terms)
          </h3>
          <p className="mt-2 text-xs text-neutral-500">Customers must accept this before paying. Blank line between paragraphs; start section headings like "9. REFUNDS".</p>
          <div className="mt-6 space-y-5">
            {LEGAL_FIELDS.map((f) => (
              <div key={f.k}>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{f.label}</label>
                <textarea rows={12} value={form[f.k] || ""} onChange={(e) => set(f.k, e.target.value)}
                  className={`${inputCls} mt-2 resize-y font-mono text-xs leading-relaxed`} data-testid={`content-${f.k}`} />
              </div>
            ))}
          </div>
        </motion.section>

        <button onClick={save} disabled={busy} data-testid="content-save-button"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-4 text-sm font-bold tracking-[0.12em] text-black shadow-[0_8px_30px_rgb(var(--gold)_/_0.25)] transition-shadow duration-300 hover:shadow-[0_8px_40px_rgb(var(--gold)_/_0.45)] disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <PenLine size={15} />}
          PUBLISH CHANGES
        </button>
      </div>
    </div>
  );
}
